import type { ObjectiveAnchor, ObjectivePeriodRule, ObjectiveShape, SavedObjective, ViewRequest } from '@api/replenishment-engine';

import { addDaysKey, dayLabel, localDateKey, monthEndKey } from './dates';

/**
 * Templates, with the words that pick each. "Orders FOR a period" are the orders behind the
 * dips in that period, placed a lead earlier; "orders TO PLACE in a period" are the ones
 * whose place-by falls in it.
 */
export const OBJECTIVE_TEMPLATES: Array<{ key: string; label: string; sentence: string; anchor: ObjectiveAnchor; shape: ObjectiveShape; periodRule: ObjectivePeriodRule; coverDays?: number }> = [
    {
        key: 'dip-lead',
        label: 'SKUs going below safety stock inside their lead time',
        sentence: 'SKUs going below safety stock inside their lead time',
        anchor: 'DIP',
        shape: 'SKU',
        periodRule: { kind: 'LEAD_TIME', plusDays: 0 },
    },
    {
        key: 'dip-60',
        label: 'SKUs going below safety stock in the next 60 days',
        sentence: 'SKUs going below safety stock in the next 60 days',
        anchor: 'DIP',
        shape: 'SKU',
        periodRule: { kind: 'RELATIVE', offsetDays: 0, lengthDays: 60 },
    },
    {
        key: 'produce-tomorrow',
        label: 'Production to start tomorrow',
        sentence: 'Production to start tomorrow',
        anchor: 'PRODUCTION_START',
        shape: 'PRODUCTION',
        periodRule: { kind: 'RELATIVE', offsetDays: 1, lengthDays: 1 },
    },
    {
        key: 'produce-14',
        label: 'Production to start in the next 14 days',
        sentence: 'Production to start in the next 14 days',
        anchor: 'PRODUCTION_START',
        shape: 'PRODUCTION',
        periodRule: { kind: 'RELATIVE', offsetDays: 0, lengthDays: 14 },
    },
    {
        key: 'rm-week-30',
        label: 'RM / PM to order this week, each order covering 30 days of production',
        sentence: 'RM / PM to order in the next 7 days, each order covering 30 days of production',
        anchor: 'MATERIAL_PLACE_BY',
        shape: 'MATERIAL',
        periodRule: { kind: 'RELATIVE', offsetDays: 0, lengthDays: 7 },
        coverDays: 30,
    },
    {
        key: 'fg-po-30',
        label: 'FG purchase orders to place in the next 30 days',
        sentence: 'FG purchase orders to place in the next 30 days',
        anchor: 'FG_PLACE_BY',
        shape: 'PURCHASE',
        periodRule: { kind: 'RELATIVE', offsetDays: 0, lengthDays: 30 },
    },
    {
        key: 'fg-po-for-30',
        label: 'FG purchase orders for SKUs dipping in the next 30 days',
        sentence: 'FG purchase orders for SKUs dipping in the next 30 days',
        anchor: 'DIP',
        shape: 'PURCHASE',
        periodRule: { kind: 'RELATIVE', offsetDays: 0, lengthDays: 30 },
    },
];

export const ANCHOR_LABEL: Record<ObjectiveAnchor, string> = {
    DIP: 'SKUs going below safety stock',
    PRODUCTION_START: 'Production to start',
    MATERIAL_PLACE_BY: 'RM / PM to order',
    FG_PLACE_BY: 'FG purchase orders to place',
};

export const SHAPE_FOR_ANCHOR: Record<ObjectiveAnchor, ObjectiveShape> = {
    DIP: 'SKU',
    PRODUCTION_START: 'PRODUCTION',
    MATERIAL_PLACE_BY: 'MATERIAL',
    FG_PLACE_BY: 'PURCHASE',
};

export const describePeriodRule = (rule: ObjectivePeriodRule): string => {
    if (rule.kind === 'LEAD_TIME') return rule.plusDays > 0 ? `inside each SKU's lead time + ${rule.plusDays} days` : "inside each SKU's lead time";
    if (rule.kind === 'FIXED') return `${dayLabel(rule.start)} → ${dayLabel(rule.end)}`;
    if (rule.lengthDays === 1) return rule.offsetDays === 0 ? 'today' : rule.offsetDays === 1 ? 'tomorrow' : `in ${rule.offsetDays} days`;
    return rule.offsetDays === 0 ? `the next ${rule.lengthDays} days` : `${rule.lengthDays} days from ${addDaysKey(localDateKey(), rule.offsetDays)}`;
};

/** What a dip-anchored objective shows when it is not the SKU table: the documents behind the dips. */
const DIP_SHAPE_SUBJECT: Record<ObjectiveShape, string> = {
    SKU: 'SKUs going below safety stock',
    PURCHASE: 'FG purchase orders for SKUs dipping',
    PRODUCTION: 'Production for SKUs dipping',
    MATERIAL: 'RM / PM for SKUs dipping',
};

/** A fixed window as words: a whole calendar month by name, else its two dates. */
const fixedWords = (rule: { start: string; end: string }): string => {
    if (rule.start.endsWith('-01') && rule.end === monthEndKey(rule.start)) {
        const [y, m] = rule.start.split('-').map(Number);
        return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    return `between ${dayLabel(rule.start)} and ${dayLabel(rule.end)}`;
};

/** The sentence an anchor, window, shape and cover mean. Variables are the truth; the sentence is their label. */
export const sentenceFor = (anchor: ObjectiveAnchor, rule: ObjectivePeriodRule, coverDays?: number | null, shape?: ObjectiveShape): string => {
    const subject = anchor === 'DIP' && shape && shape !== 'SKU' ? DIP_SHAPE_SUBJECT[shape] : ANCHOR_LABEL[anchor];
    const cover = anchor === 'MATERIAL_PLACE_BY' && coverDays ? `, each order covering ${coverDays} days of production` : '';
    if (rule.kind === 'LEAD_TIME') return `${subject} ${describePeriodRule(rule)}${cover}`;
    if (rule.kind === 'FIXED') return `${subject} ${rule.start.endsWith('-01') && rule.end === monthEndKey(rule.start) ? 'in ' : ''}${fixedWords(rule)}${cover}`;
    if (rule.lengthDays === 1) return `${subject} ${describePeriodRule(rule)}${cover}`;
    return `${subject} in ${describePeriodRule(rule)}${cover}`;
};

/** A saved objective as the view request it means today. */
export const requestFromSaved = (o: SavedObjective, today = localDateKey()): ViewRequest => {
    const base = { anchor: o.anchor, shape: o.shape, objectiveId: o.id };
    const levels = {
        ...(o.previewLevels?.levelDays != null ? { levelDays: o.previewLevels.levelDays } : {}),
        ...(o.previewLevels?.refillDays != null ? { refillDays: o.previewLevels.refillDays } : {}),
    };
    if (o.periodRule.kind === 'LEAD_TIME') return { ...base, periodStart: today, periodEnd: today, leadTimePlusDays: o.periodRule.plusDays, ...levels };
    if (o.periodRule.kind === 'FIXED') return { ...base, periodStart: o.periodRule.start, periodEnd: o.periodRule.end, ...levels };
    const start = addDaysKey(today, o.periodRule.offsetDays);
    return { ...base, periodStart: start, periodEnd: addDaysKey(start, Math.max(1, o.periodRule.lengthDays) - 1), ...levels };
};

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

/**
 * A sentence read into the engine's variables. Heuristic on purpose: the result is shown
 * back as controls the user can correct before adding, never acted on blind.
 */
export const parseObjectiveSentence = (sentence: string, today = localDateKey()): { anchor: ObjectiveAnchor; shape: ObjectiveShape; periodRule: ObjectivePeriodRule; coverDays: number | null; cues: string[] } => {
    let s = sentence.toLowerCase();
    const cues: string[] = [];
    // "covering 30 days of production" is the sizing period at the material node, not the window.
    let coverDays: number | null = null;
    const cover = s.match(/\b(?:cover|covers|covering|to cover)\s+(?:the\s+)?(?:next\s+)?(\d+)\s+(day|week|month)s?(?:\s+of\s+production)?/);
    if (cover) {
        coverDays = Number(cover[1]) * (cover[2].startsWith('week') ? 7 : cover[2].startsWith('month') ? 30 : 1);
        s = s.replace(cover[0], ' ');
        cues.push(`covering ${coverDays} days of production → one material order per ${coverDays} days of runs`);
    }
    // The table is picked by the thing named; the anchor by the verb. "Orders to place in
    // December" picks rows by their place-by; "orders for December" picks the dips in
    // December and shows the orders behind them, placed a lead earlier.
    const toAct = /\b(to place|to order|to start|to produce|to make|to buy|place by|start by|order by|placed|started)\b/.test(s);
    let anchor: ObjectiveAnchor = 'DIP';
    let shape: ObjectiveShape = 'SKU';
    if (/\b(rm|pm|raw material|packaging|material)s?\b/.test(s)) {
        shape = 'MATERIAL';
        anchor = toAct ? 'MATERIAL_PLACE_BY' : 'DIP';
        cues.push(toAct ? 'material + to order → RM / PM orders by place-by' : 'material, no verb → RM / PM behind the dips in the period');
    } else if (/\b(fg|finished good)s?\b.*\b(po|purchase|order|buy)/.test(s) || /\b(purchase orders?|buy|pos?)\b/.test(s)) {
        shape = 'PURCHASE';
        anchor = toAct ? 'FG_PLACE_BY' : 'DIP';
        cues.push(toAct ? 'purchase + to place → FG purchase orders by place-by' : 'purchase, no verb → FG purchase orders behind the dips in the period');
    } else if (/\b(produc|make|manufactur|work order|batch)/.test(s)) {
        shape = 'PRODUCTION';
        anchor = toAct ? 'PRODUCTION_START' : 'DIP';
        cues.push(toAct ? 'produce + to start → production starts' : 'produce, no verb → production behind the dips in the period');
    } else {
        cues.push('no production / order word → SKUs going below safety stock');
    }

    let periodRule: ObjectivePeriodRule | null = null;
    const unitDays = (u: string): number => (u.startsWith('week') ? 7 : u.startsWith('month') ? 30 : 1);
    let m: RegExpMatchArray | null;
    if ((m = s.match(/lead ?time(?:\s*(?:\+|plus)\s*(\d+))?/))) {
        periodRule = { kind: 'LEAD_TIME', plusDays: m[1] ? Number(m[1]) : 0 };
        cues.push('lead time → per-SKU window');
    } else if (/\btomorrow\b/.test(s)) {
        periodRule = { kind: 'RELATIVE', offsetDays: 1, lengthDays: 1 };
        cues.push('tomorrow → one day, starting tomorrow');
    } else if (/\btoday\b/.test(s)) {
        periodRule = { kind: 'RELATIVE', offsetDays: 0, lengthDays: 1 };
        cues.push('today → one day');
    } else if ((m = s.match(/next (\d+) (day|week|month)s?/))) {
        periodRule = { kind: 'RELATIVE', offsetDays: 0, lengthDays: Number(m[1]) * unitDays(m[2]) };
        cues.push(`next ${m[1]} ${m[2]}s → ${Number(m[1]) * unitDays(m[2])} days from today`);
    } else if ((m = s.match(/\b(this|next) (week|month)\b/))) {
        const len = unitDays(m[2]);
        periodRule = { kind: 'RELATIVE', offsetDays: m[1] === 'next' ? len : 0, lengthDays: len };
        cues.push(`${m[1]} ${m[2]} → ${len} days`);
    } else if ((m = s.match(new RegExp(`\\b(${MONTHS.join('|')})\\b(?:\\s*(\\d{4}))?`)))) {
        const month = MONTHS.indexOf(m[1]);
        const now = new Date(`${today}T00:00:00`);
        let year = m[2] ? Number(m[2]) : now.getFullYear();
        if (!m[2] && month < now.getMonth()) year += 1;
        const start = `${year}-${String(month + 1).padStart(2, '0')}-01`;
        const end = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
        periodRule = { kind: 'FIXED', start, end };
        cues.push(`${m[1]} → fixed window ${start} to ${end}`);
    } else if ((m = s.match(/(\d+) (day|week|month)s?/))) {
        periodRule = { kind: 'RELATIVE', offsetDays: 0, lengthDays: Number(m[1]) * unitDays(m[2]) };
        cues.push(`${m[1]} ${m[2]}s → ${Number(m[1]) * unitDays(m[2])} days from today`);
    }
    if (!periodRule) {
        periodRule = { kind: 'RELATIVE', offsetDays: 0, lengthDays: 30 };
        cues.push('no period word → the next 30 days');
    }
    if (coverDays != null && anchor !== 'MATERIAL_PLACE_BY') {
        cues.push('a cover only applies to RM / PM orders; ignored here');
        coverDays = null;
    }
    return { anchor, shape, periodRule, coverDays, cues };
};

export const SHAPE_LABEL: Record<ObjectiveShape, string> = {
    SKU: 'SKU table: one row per SKU with its dip, start, material, transfer and orders',
    PRODUCTION: 'Production table: one row per work order, grouped by factory',
    MATERIAL: 'Material table: RM / PM orders and transfers, grouped by vendor',
    PURCHASE: 'Purchase table: FG purchase orders, grouped by vendor',
};

export const ANCHOR_MEANING: Record<ObjectiveAnchor, string> = {
    DIP: 'the day the SKU first falls below safety stock',
    PRODUCTION_START: 'the day production must start',
    MATERIAL_PLACE_BY: 'the day the RM / PM order must be placed',
    FG_PLACE_BY: 'the day the FG purchase order must be placed',
};

/** The concrete window an objective means today. */
export const windowOf = (rule: ObjectivePeriodRule, today = localDateKey()): { start: string; end: string | null; perSku: boolean } => {
    if (rule.kind === 'LEAD_TIME') return { start: today, end: null, perSku: true };
    if (rule.kind === 'FIXED') return { start: rule.start, end: rule.end, perSku: false };
    const start = addDaysKey(today, rule.offsetDays);
    return { start, end: addDaysKey(start, Math.max(1, rule.lengthDays) - 1), perSku: false };
};

/**
 * How far this objective makes the engine look, mirroring the backend. A dip inside the
 * window is acted on today at the earliest, so its order lands on max(dip, lead); the curve
 * is needed to that landing plus R to size the order. An action anchor's dip is up to one
 * chain lead after the window. The brand's longest lead and refill stand in for the
 * per-SKU values the run uses.
 */
export const horizonEstimate = (rule: ObjectivePeriodRule, anchor: ObjectiveAnchor, cap: number, maxLead: number, maxRefill: number, today = localDateKey(), coverDays = 0): { days: number; parts: string[] } => {
    const windowEnd =
        rule.kind === 'LEAD_TIME' ? maxLead + rule.plusDays : rule.kind === 'FIXED' ? Math.max(0, Math.round((new Date(`${rule.end}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000)) : rule.offsetDays + Math.max(1, rule.lengthDays) - 1;
    const parts: string[] = [];
    let landEnd: number;
    if (anchor === 'DIP') {
        landEnd = Math.max(windowEnd, maxLead);
        parts.push(rule.kind === 'LEAD_TIME' ? `find dips out to each SKU's lead time (longest ${maxLead}d${rule.plusDays ? ` + ${rule.plusDays}d` : ''})` : `find dips out to day ${windowEnd}`);
        if (windowEnd < maxLead) parts.push(`an order for a dip in that window lands no later than the lead time, ${maxLead}d`);
    } else {
        const cover = anchor === 'MATERIAL_PLACE_BY' ? Math.max(0, coverDays) : 0;
        landEnd = windowEnd + maxLead + cover;
        parts.push(`actions out to day ${windowEnd}`, `+ ${maxLead}d lead to the dip they serve`);
        if (cover > 0) parts.push(`+ ${cover}d of runs one material order covers`);
    }
    parts.push(`+ ${maxRefill}d R to size the order from its landing`);
    const days = Math.min(cap, Math.max(30, landEnd + maxRefill + 1));
    if (days === cap) parts.push(`capped at ${cap}d`);
    return { days, parts };
};
